import { dateOnly, timeInTz } from '../../lib/format';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { StatusBadge } from '../ui/Badge';
import { VISIT_STATUS, display } from '../status';
import { IconChevronLeft, IconChevronRight } from '../icons';
import {
  UNASSIGNED_LANE,
  cellAt,
  hoursLabel,
  laneWeekTotals,
  type WeekGrid,
} from './weekGrid';

/**
 * The week board's panel — **three levels, one component** (120 A10 + A11).
 *
 * 🟢 **REUSE, not a fifth pattern.** This is `ui/Sheet` again, the same panel 104 shipped
 * against Sonar's side-panel anatomy and which the day board's cards, map pins and table rows
 * already open. The week board is a fourth opener of it.
 *
 * | Click | The panel is about | Header |
 * |---|---|---|
 * | a lane | that tech's **week** | "Maya Alvarez · Mon 24 – Sun 30 Aug" |
 * | a cell | that tech's **day** | "Maya Alvarez · Thu 27 Aug" |
 * | a stop | that **visit** | the job title |
 *
 * ⚠️ **A cell does not navigate.** A11 supersedes decision 5: the cell is the densest target
 * on the grid, and it would have been the one element that jumped pages while its neighbours
 * slid a panel open. The day board is not lost — it is demoted to the footer action, per
 * 104's idiom that a panel is a transient preview whose footer goes to the URL-real page.
 *
 * ⚠️ **Back, not a dead end.** Stop is reached from day, day from week, so the header carries
 * a way back up rather than making the viewer close and re-open from the grid.
 */

export type WeekPanelTarget =
  | { level: 'week'; laneId: string }
  | { level: 'day'; laneId: string; day: string }
  | { level: 'stop'; laneId: string; day: string; visitId: string };

/** What the panel needs to know about one visit — a subset of the board row. */
export interface PanelVisit {
  id: string;
  status: string;
  scheduledStartAt: string | Date | null;
  jobTitle: string;
  customerName: string;
  address: string | null;
}

export function WeekPanel({
  target,
  grid,
  visitsById,
  tz,
  onClose,
  onNavigate,
  onOpenDayBoard,
}: {
  target: WeekPanelTarget | null;
  grid: WeekGrid;
  visitsById: Map<string, PanelVisit>;
  tz: string;
  onClose: () => void;
  onNavigate: (next: WeekPanelTarget) => void;
  /** The URL-real page this preview defers to — 104's footer contract. */
  onOpenDayBoard: (day: string) => void;
}) {
  const lane = target ? grid.lanes.find((l) => l.laneId === target.laneId) : undefined;
  if (!target || !lane) {
    return <Sheet open={false} onClose={onClose} title="" children={null} />;
  }

  const dayLabel = (d: string) =>
    dateOnly(d, { weekday: 'short', day: 'numeric', month: 'short' });
  const stopsIn = (day: string) =>
    (cellAt(grid, target.laneId, day)?.visitIds ?? [])
      .map((id) => visitsById.get(id))
      .filter((v): v is PanelVisit => Boolean(v));

  /** A stop row — the one place a stop is clickable on this board (A10). */
  const StopRow = ({ v, day }: { v: PanelVisit; day: string }) => {
    const s = display(VISIT_STATUS, v.status);
    return (
      <li>
        <button
          type="button"
          onClick={() =>
            onNavigate({ level: 'stop', laneId: target.laneId, day, visitId: v.id })
          }
          className="flex min-h-11 w-full items-center gap-2 rounded-[var(--radius)] px-2 text-left hover:bg-surface-2"
        >
          <span className="w-16 shrink-0 font-mono text-xs tabular-nums">
            {v.scheduledStartAt ? timeInTz(v.scheduledStartAt, tz) : '—'}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">{v.customerName}</span>
            <span className="block truncate text-xs text-muted-foreground">
              {v.jobTitle}
            </span>
          </span>
          <StatusBadge tone={s.tone}>{s.label}</StatusBadge>
          <IconChevronRight size={15} className="shrink-0 text-muted-foreground" />
        </button>
      </li>
    );
  };

  const back = (to: WeekPanelTarget, label: string) => (
    <Button variant="ghost" className="min-h-11" onClick={() => onNavigate(to)}>
      <IconChevronLeft size={15} /> {label}
    </Button>
  );

  if (target.level === 'week') {
    const t = laneWeekTotals(grid, target.laneId);
    const first = grid.days[0]!;
    const last = grid.days[grid.days.length - 1]!;
    return (
      <Sheet
        open
        onClose={onClose}
        title={lane.name}
        badge={
          lane.vanless && lane.userId ? (
            <StatusBadge tone="warn">No van</StatusBadge>
          ) : null
        }
        footer={
          <Button
            className="w-full"
            onClick={() => onOpenDayBoard(t.busiestDay ?? first)}
          >
            Open this week on the board
          </Button>
        }
      >
        <p className="text-sm text-muted-foreground">
          {dayLabel(first)} – {dayLabel(last)}
        </p>
        <dl className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <dt className="text-xs text-muted-foreground">Stops</dt>
            <dd className="font-heading text-2xl font-bold tabular-nums">{t.stops}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Booked</dt>
            <dd className="font-heading text-2xl font-bold tabular-nums">
              {hoursLabel(t.bookedMinutes)}
            </dd>
          </div>
        </dl>
        {/* The day-by-day shape — the panel's own version of the lane, so a reader can see
            where the week is heavy without going back to the grid. */}
        <ul className="mt-4 space-y-1">
          {grid.days.map((d) => {
            const c = cellAt(grid, target.laneId, d);
            return (
              <li key={d}>
                <button
                  type="button"
                  disabled={!c || c.stops === 0}
                  onClick={() =>
                    onNavigate({ level: 'day', laneId: target.laneId, day: d })
                  }
                  className="flex min-h-11 w-full items-center justify-between gap-2 rounded-[var(--radius)] px-2 text-left text-sm hover:bg-surface-2 disabled:opacity-50 disabled:hover:bg-transparent"
                >
                  <span>{dayLabel(d)}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {c && c.stops > 0
                      ? `${c.stops} · ${hoursLabel(c.bookedMinutes)}`
                      : '—'}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </Sheet>
    );
  }

  if (target.level === 'day') {
    const cell = cellAt(grid, target.laneId, target.day);
    const stops = stopsIn(target.day);
    return (
      <Sheet
        open
        onClose={onClose}
        title={`${lane.name} · ${dayLabel(target.day)}`}
        actions={back({ level: 'week', laneId: target.laneId }, 'Week')}
        footer={
          <Button className="w-full" onClick={() => onOpenDayBoard(target.day)}>
            Open {dateOnly(target.day, { weekday: 'long' })} on the board
          </Button>
        }
      >
        <p className="text-sm text-muted-foreground">
          {cell
            ? `${cell.stops} stops · ${hoursLabel(cell.bookedMinutes)} booked`
            : 'Nothing booked'}
        </p>
        {/*
          ⚠️ The van for THIS day, if the lane has one. The lane header cannot name a van
          honestly across a week — a tech can change vans mid-week — so the per-day fact
          lives here, which is 121's ruling on what a lane means.
        */}
        <ul className="mt-3 space-y-1">
          {stops.map((v) => (
            <StopRow key={v.id} v={v} day={target.day} />
          ))}
        </ul>
      </Sheet>
    );
  }

  const v = visitsById.get(target.visitId);
  const s = v ? display(VISIT_STATUS, v.status) : null;
  return (
    <Sheet
      open
      onClose={onClose}
      title={v?.jobTitle ?? 'Stop'}
      badge={s ? <StatusBadge tone={s.tone}>{s.label}</StatusBadge> : null}
      actions={back(
        { level: 'day', laneId: target.laneId, day: target.day },
        dateOnly(target.day, { weekday: 'short' }),
      )}
      footer={
        <Button className="w-full" onClick={() => onOpenDayBoard(target.day)}>
          Open {dateOnly(target.day, { weekday: 'long' })} on the board
        </Button>
      }
    >
      {v ? (
        <dl className="space-y-2 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">Customer</dt>
            <dd className="font-medium">{v.customerName}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">When</dt>
            <dd className="tabular-nums">
              {v.scheduledStartAt ? timeInTz(v.scheduledStartAt, tz) : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Where</dt>
            <dd>{v.address ?? '—'}</dd>
          </div>
        </dl>
      ) : (
        // The row set moved under the panel — a refetch dropped this visit. Say so rather
        // than rendering a shell of empty fields.
        <p className="text-sm text-muted-foreground">
          This stop is no longer on the board. It may have been moved or canceled.
        </p>
      )}
    </Sheet>
  );
}

/** Exported for the page's guard: the unassigned lane has no week summary worth opening. */
export const PANEL_SKIPS = UNASSIGNED_LANE;
